import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Copy, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { http, formatApiError } from "@/lib/api";
import { RosterTable } from "@/components/teacher/RosterTable";
import { AttendanceGrid } from "@/components/teacher/AttendanceGrid";
import { ReflectionCard } from "@/components/teacher/ReflectionCard";
import { AssignedLessons } from "@/components/teacher/AssignedLessons";
import { useAssessmentAlerts, AlertList } from "@/components/assessment/AssessmentAlerts";
import { Gradebook } from "@/components/teacher/Gradebook";
import { EDITIONS } from "@/lib/brand";

const Settings = ({ cls, onSaved }) => {
  const [mins, setMins] = useState(cls.min_minutes);
  const save = async () => {
    try { await http.patch(`/classes/${cls.id}`, { min_minutes: Number(mins) }); toast.success("Attendance minimum updated"); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-stone-500">Present after</span>
      <input type="number" min={1} max={480} value={mins} onChange={(e) => setMins(e.target.value)} data-testid="class-min-minutes-input"
        className="w-20 px-3 py-2 rounded-full bg-white border border-[#E6DEC3] text-center focus:outline-none focus:border-[#2C4A3E]" />
      <span className="text-stone-500">min/day</span>
      <button onClick={save} disabled={Number(mins) === cls.min_minutes} data-testid="class-min-minutes-save-btn" className="px-4 py-2 rounded-full bg-[#2C4A3E] text-white disabled:opacity-40">Save</button>
    </div>
  );
};

const ClassEdition = ({ cls, conflicts, onSaved }) => {
  const [st, setSt] = useState(cls.state || "");
  const save = async () => {
    try { await http.patch(`/classes/${cls.id}`, { state: st }); toast.success(`${EDITIONS[st]} set for every student in ${cls.name}`); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="mt-3" data-testid="class-edition">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-stone-500">Class edition</span>
        <select value={st} onChange={(e) => setSt(e.target.value)} data-testid="class-edition-select" className="px-3 py-2 rounded-full bg-white border border-[#E6DEC3] focus:outline-none focus:border-[#2C4A3E]">
          {!cls.state && <option value="">Not set (students keep their own)</option>}
          {Object.entries(EDITIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        <button onClick={save} disabled={!st || st === cls.state} data-testid="class-edition-save-btn" className="px-4 py-2 rounded-full bg-[#2C4A3E] text-white disabled:opacity-40">Apply to class</button>
        <span className="text-xs text-stone-500">Students (and anyone who joins later) follow this edition. Parents see it was set by you.</span>
      </div>
      {conflicts?.length > 0 && <div className="mt-3 rounded-2xl bg-[#FBF3E6] border border-[#C87D53]/30 p-4 text-sm" data-testid="class-edition-conflicts">
        <p className="font-medium text-[#7A4A12]">Some students are also in a class that uses a different edition. The most recently saved class edition applies.</p>
        <ul className="mt-2 space-y-1 text-stone-700">{conflicts.map((c) => <li key={`${c.student_id}-${c.other_class}`} data-testid={`class-edition-conflict-${c.student_id}`}>{c.student}: {c.other_class} uses {EDITIONS[c.other_state]} · now on {EDITIONS[c.current_state] || "Georgia Edition"}</li>)}</ul>
      </div>}
    </div>
  );
};

const ClassAlerts = ({ classId }) => {
  const [alerts, resolve] = useAssessmentAlerts(classId);
  if (!alerts.length) return null;
  return (
    <div className="mb-6 rounded-3xl bg-[#FBF3E6] border border-[#C87D53]/30 p-5" data-testid="class-assess-alerts">
      <p className="font-display text-lg text-[#2C4A3E] mb-3">Check before assigning</p>
      <AlertList alerts={alerts} resolve={resolve} testId="class-assess-alert-list" />
    </div>
  );
};

export default function ClassDetail() {
  const { classId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [reflections, setReflections] = useState([]);
  const [tab, setTab] = useState("students");

  const load = useCallback(() => {
    http.get(`/classes/${classId}/overview`).then((r) => setData(r.data)).catch(() => nav("/teacher"));
    http.get(`/classes/${classId}/reflections`).then((r) => setReflections(r.data));
  }, [classId, nav]);
  useEffect(load, [load]);

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name} from this class?`)) return;
    await http.delete(`/classes/${classId}/students/${s.id}`); toast.success(`${s.name} removed`); load();
  };
  const del = async () => {
    if (!window.confirm("Delete this class? Student accounts stay intact.")) return;
    await http.delete(`/classes/${classId}`); toast.success("Class deleted"); nav("/teacher");
  };
  const copy = () => { navigator.clipboard?.writeText(data.class.join_code); toast.success("Join code copied"); };

  if (!data) return <div className="max-w-6xl mx-auto px-6 py-20 text-stone-500">Loading…</div>;
  const presentCount = data.students.filter((s) => s.present_today).length;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12" data-testid="class-detail-page">
      <Link to="/teacher" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#2C4A3E]" data-testid="class-back-link"><ArrowLeft className="w-4 h-4" /> All classes</Link>
      <div className="mt-6 flex flex-wrap justify-between items-end gap-6">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#2C4A3E]" data-testid="class-title">{data.class.name}</h1>
          <p className="text-stone-500 mt-2">{data.students.length} students · <span data-testid="class-present-count">{presentCount} present today</span></p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={copy} data-testid="class-copy-code-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F7F3EB] border border-[#E6DEC3] font-mono tracking-widest text-[#2C4A3E]">
            <span data-testid="class-join-code">{data.class.join_code}</span> <Copy className="w-4 h-4" />
          </button>
          <button onClick={del} data-testid="class-delete-btn" className="p-2.5 rounded-full border border-[#E6DEC3] text-stone-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="mt-6"><Settings key={data.class.min_minutes} cls={data.class} onSaved={load} /><ClassEdition key={data.class.state || "none"} cls={data.class} conflicts={data.edition_conflicts} onSaved={load} /></div>
      <Tabs value={tab} onValueChange={setTab} className="mt-10">
        <TabsList className="bg-[#F1EADB] rounded-3xl sm:rounded-full p-1 h-auto flex-wrap justify-start">
          {["students", "lessons", "gradebook", "attendance", "reflections"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`class-tab-${t}`} className="rounded-full px-5 py-2 capitalize data-[state=active]:bg-[#2C4A3E] data-[state=active]:text-white">{t}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="students" className="mt-6"><RosterTable classId={classId} students={data.students} onRemove={remove} /></TabsContent>
        <TabsContent value="lessons" className="mt-6"><ClassAlerts classId={classId} /><AssignedLessons classId={classId} /></TabsContent>
        <TabsContent value="gradebook" className="mt-6"><Gradebook classId={classId} className={data.class.name} /></TabsContent>
        <TabsContent value="attendance" className="mt-6"><AttendanceGrid key={data.class.min_minutes} classId={classId} className={data.class.name} /></TabsContent>
        <TabsContent value="reflections" className="mt-6">
          <div className="grid md:grid-cols-2 gap-5" data-testid="class-reflections">
            {reflections.map((e) => <ReflectionCard key={e.id} e={e} showStudent />)}
          </div>
          {!reflections.length && <p className="text-stone-500 text-center py-10" data-testid="class-reflections-empty">No reflections yet.</p>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
